import React, { createContext, useContext, useState, useRef, useCallback, ReactNode } from "react";

interface AgentCanvasState {
  isOpen: boolean;
  htmlContent: string;
  isStreaming: boolean;
  openCanvas: (content: string, streaming: boolean) => void;
  closeCanvas: () => void;
  updateCanvasContent: (
    content: string,
    streaming: boolean,
    messageId?: string,
    shouldAutoOpen?: boolean
  ) => void;
  actionHandler: ((action: string, payload: any) => void) | null;
  setActionHandler: (handler: ((action: string, payload: any) => void) | null) => void;
  terminateCanvas: () => void;
}

const AgentCanvasContext = createContext<AgentCanvasState | undefined>(undefined);

export function AgentCanvasProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [actionHandler, setActionHandler] = useState<
    ((action: string, payload: any) => void) | null
  >(null);

  // Use a ref for autoOpenedIds so it doesn't trigger re-renders or require dependency updates
  const autoOpenedIds = useRef<Set<string>>(new Set());

  const openCanvas = useCallback((content: string, streaming: boolean) => {
    setHtmlContent(content);
    setIsStreaming(streaming);
    setIsOpen(true);
  }, []);

  const closeCanvas = useCallback(() => {
    setIsOpen(false);
    // Don't clear content so it can be re-opened
  }, []);

  const terminateCanvas = useCallback(() => {
    setIsOpen(false);
    setHtmlContent("");
    setIsStreaming(false);
  }, []);

  const updateCanvasContent = useCallback(
    (content: string, streaming: boolean, messageId?: string, shouldAutoOpen: boolean = true) => {
      setHtmlContent(content);
      setIsStreaming(streaming);

      // Ensure we only auto-open once per unique message, and only if requested (e.g., new messages)
      if (shouldAutoOpen && messageId && !autoOpenedIds.current.has(messageId)) {
        autoOpenedIds.current.add(messageId);
        setIsOpen(true);
      }
    },
    []
  );

  const value = React.useMemo(
    () => ({
      isOpen,
      htmlContent,
      isStreaming,
      openCanvas,
      closeCanvas,
      updateCanvasContent,
      actionHandler,
      setActionHandler,
      terminateCanvas,
    }),
    [
      isOpen,
      htmlContent,
      isStreaming,
      openCanvas,
      closeCanvas,
      updateCanvasContent,
      actionHandler,
      terminateCanvas,
    ]
  );

  return <AgentCanvasContext.Provider value={value}>{children}</AgentCanvasContext.Provider>;
}

export function useAgentCanvas() {
  const context = useContext(AgentCanvasContext);
  if (context === undefined) {
    throw new Error("useAgentCanvas must be used within an AgentCanvasProvider");
  }
  return context;
}
